import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { HtmlHTMLAttributes, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'rsuite';
import { Layout } from '../../../../../components';
import { BigNumbersBox } from '../../../../../components/BigNumbersBox';
import { LoadingSpinerLocal } from '../../../../../components/LoadingSpinner';
import { selectorShouldUpdateBigNumbers, SET_SHOULD_UPDATE_BIG_NUMBERS } from '../../../../../reducers/gerenciadorPrecos/bigNumbers/shouldUpdateBigNumbers';
import { checkIfIsLoading } from '../../../../../utils';
import { PriceManagerDatapointsModel } from '../../hooks/usePriceManagerDatapointsModel';
import { totalPriceQuery } from '../../queries/bigNumbers/totalPrice';
import { variationPriceQuery } from '../../queries/bigNumbers/variationPrice';
import { buildCounterPills, buildIndicatorCards, PROJECTION_DAYS } from './bigNumbers.utils';
import { CounterPill, IndicatorCard, ProjectionPeriodIndicator } from './elements';
import styles from './styles.module.scss';

interface BigNumbersAreaProps extends HtmlHTMLAttributes<HTMLElement> {
  priceManagerDatapointsModel?: PriceManagerDatapointsModel;
}

const BigNumbersArea = memo(({ className, priceManagerDatapointsModel, ...props }: BigNumbersAreaProps) => {
  const dispatch = useDispatch();

  const {
    data: variationPriceData,
    isError: variationError,
    refetch: refetchVariationPriceData,
    fetchStatus: variationLoading,
  } = useQuery(variationPriceQuery(priceManagerDatapointsModel));

  const {
    data: totalPriceData,
    isError: totalError,
    refetch: refetchTotalPrices,
    fetchStatus: totalLoading,
  } = useQuery(totalPriceQuery(priceManagerDatapointsModel));

  const shouldUpdate = useSelector(selectorShouldUpdateBigNumbers);

  const counterPills = useMemo(() => buildCounterPills(totalError ? undefined : totalPriceData), [totalError, totalPriceData]);

  const indicatorCards = useMemo(
    () => buildIndicatorCards(variationError ? undefined : variationPriceData, PROJECTION_DAYS),
    [variationError, variationPriceData],
  );

  const onClickUpdate = useCallback(() => {
    dispatch(SET_SHOULD_UPDATE_BIG_NUMBERS(false));
    refetchVariationPriceData();
    refetchTotalPrices();
  }, [dispatch, refetchTotalPrices, refetchVariationPriceData]);

  return (
    <Layout.Section className={classNames(styles['big-numbers-container'], className)} {...props}>
      <div className={styles['counters-row']} data-testid="big-numbers-counters">
        <div className={styles['counters-pills']}>
          <div className={styles['pills-loading']}>
            <LoadingSpinerLocal size="sm" position="absolute" loading={checkIfIsLoading(totalLoading)} />
          </div>
          {counterPills.map(pill => (
            <CounterPill key={pill.id} {...pill} />
          ))}
        </div>
        <ProjectionPeriodIndicator days={PROJECTION_DAYS} />
      </div>
      <div className={styles['indicators-row']} data-testid="big-numbers-indicators">
        {indicatorCards.map(card => (
          <IndicatorCard key={card.id} card={card} loading={checkIfIsLoading(variationLoading)} />
        ))}
        {shouldUpdate ? (
          <Button appearance="default" className={styles['update-button']} onClick={onClickUpdate}>
            <BigNumbersBox.RegularText>Cálculos</BigNumbersBox.RegularText>
            <BigNumbersBox.RegularText>desatualizados</BigNumbersBox.RegularText>
            <p className={styles['blue-text']}>Atualizar</p>
          </Button>
        ) : null}
      </div>
    </Layout.Section>
  );
});

BigNumbersArea.displayName = 'BigNumbersArea';

export default BigNumbersArea;
